import { z } from "zod";
import { LeadSourceSchema } from "./adminClientData";
import { NameSchema, OptionalPhoneSchema, PhoneSchema, UserIdNumberSchema } from "./user";
import { birthDateSchema, bituahProviderOptions, sexOptions } from "./utils";
import { DocumentSchema, DocumentTypeSchema } from "./document";
import { ProfileLeadStatusSchema } from "./profileLeadStatus";

/** Shared by every form that collects a client email. Optional on the profile
 *  itself: an admin opening a patient by hand often has no email yet, and the
 *  profile must not be blocked on it. Signup re-requires it (RegisterSchema). */
export const ClientEmailSchema = z
	.string()
	.email({ message: "אמייל לא תקין" })
	.transform((str) => str.trim());

// A cleared form input is an absent email, not an invalid address.
export const OptionalClientEmailSchema = z.string().optional()
	.transform((value) => value?.trim() || undefined)
	.pipe(ClientEmailSchema.optional());

/** Identity fields as they are STORED: a valid value or absent, never blank. An
 *  admin opening a client by hand may know only a name, only a phone, or only
 *  an ID number — every one of these is optional on the profile and can be
 *  completed later from "ערוך". Forms that must insist on them do so by
 *  re-requiring (RegisterSchema) or .required() (personalInfoSchema). Blank →
 *  absent is handled at the form/callable boundary (see Optional*Schema below),
 *  so these stay plain ZodOptional and .required() can unwrap them. */
export const ProfileSchema = z.object({
	id: z.string(),
	email: OptionalClientEmailSchema,
	IDNumber: UserIdNumberSchema.transform((str) => str.trim()).optional(),
	firstName: NameSchema.transform((str) => str.trim()).optional(),
	lastName: NameSchema.transform((str) => str.trim()).optional(),
	phone: PhoneSchema.transform((str) => str?.trim()).optional(),
	phone2: OptionalPhoneSchema.transform((str) => str?.trim()),
	documents: z
		.object({
			[DocumentTypeSchema.Values.ID]: DocumentSchema.optional(),
			[DocumentTypeSchema.Values.CANNABIS_LICENSE]: DocumentSchema.optional(),
			[DocumentTypeSchema.Values.PRESCRIPTIONS]: DocumentSchema.optional(),
			[DocumentTypeSchema.Values.DOCTOR_RECOMMENDATION]: DocumentSchema.optional(),
		})
		.optional(),
	dob: birthDateSchema.optional(),
	sex: z
		.enum(sexOptions, {
			errorMap: () => ({ message: "יש לבחור מין" }),
		})
		.optional(),
	healthInsurance: z
		.enum(bituahProviderOptions, {
			errorMap: () => ({ message: "יש לבחור גורם מבטח" }),
		})
		.optional(),
	city: z
		.string({
			errorMap: () => ({ message: "יש להזין ישוב" }),
		})
		.min(2, {
			message: "יש להזין ישוב",
		})
		.transform((val) => val.trim())
		.optional(),
	street: z
		.string({
			errorMap: () => ({ message: "יש להזין רחוב" }),
		})
		.min(2, {
			message: "יש להזין רחוב",
		})
		.transform((val) => val.trim())
		.optional(),
	houseNumber: z
		.string({
			errorMap: () => ({ message: "יש להזין מספר בית" }),
		})
		.min(1, {
			message: "יש להזין מספר בית",
		})
		.transform((val) => val.trim())
		.optional(),
	apartmentNumber: z
		.string({
			errorMap: () => ({ message: "יש להזין מספר דירה" }),
		})
		.transform((val) => val?.trim?.() ?? "")
		.optional(),
	coinsBalance: z.number().min(0).default(0).optional(),
	joinLinkLastSentAt: z.number().optional(),
	// Admin-managed relationship status. nullish = not set yet (backwards-compat
	// with existing profile docs that pre-date this field).
	status: ProfileLeadStatusSchema.nullish(),
});

/** Blank means "not filled in yet", not invalid — see newClientSchema below. */
const optionalAddressText = z
	.string()
	.optional()
	.transform((value) => value?.trim() || undefined);

/** Blank → absent, then the strict validator. Same shape as OptionalClientEmailSchema:
 *  an untouched input arrives as "" and must mean "not provided", while anything
 *  non-blank still has to be a real value. Used by every form and callable that
 *  accepts these fields, so a partially-filled client is valid but a mistyped one
 *  is not. */
const blankToAbsent = (value: string | undefined) => value?.trim() || undefined;
export const OptionalIdNumberSchema = z.string().optional().transform(blankToAbsent).pipe(UserIdNumberSchema.optional());
export const OptionalNameSchema = z.string().optional().transform(blankToAbsent).pipe(NameSchema.optional());
export const OptionalPhoneInputSchema = z.string().optional().transform(blankToAbsent).pipe(PhoneSchema.optional());

export const newClientSchema = z.object({
	email: OptionalClientEmailSchema,
	IDNumber: OptionalIdNumberSchema,
	firstName: OptionalNameSchema,
	lastName: OptionalNameSchema,
	phone: OptionalPhoneInputSchema,
	phone2: OptionalPhoneSchema.transform((str) => str?.trim()).optional(),
	// Everything below is optional on purpose: an admin opening a client by hand
	// fills in what they know and completes the rest later from the profile.
	// Reusing ProfileSchema's own fields keeps the create form and the profile
	// from drifting apart.
	healthInsurance: ProfileSchema.shape.healthInsurance,
	dob: ProfileSchema.shape.dob,
	sex: ProfileSchema.shape.sex,
	// NOT reused from ProfileSchema: those carry min-length rules meant for the
	// request flow, where an address is mandatory. Here an untouched input
	// arrives as "" and would fail validation ("יש להזין ישוב") on a client the
	// admin simply has no address for yet.
	city: optionalAddressText,
	street: optionalAddressText,
	houseNumber: optionalAddressText,
	apartmentNumber: optionalAddressText,
	// Lives on admin_client_data, not the profile — createClient writes it there
	// alongside the note.
	leadSource: LeadSourceSchema.optional(),
	// Same as on a lead: admins usually open clients manually and want to note
	// who the patient is right away. Saved to admin_client_data.adminComment.
	notes: z.string().optional(),
	status: ProfileLeadStatusSchema.optional(),
});

export type TNewClient = z.infer<typeof newClientSchema>;
